import PINBOARD_REGISTRY from '@/components/pinning/pinboard-registry';
import ls from '@/helpers/localStorage';

const LS_KEY = 'pinning-state';
const PANEL_ORDER_KEY = 'pinning-panel-order';

// shape: { "widget": { panel: "left"|"right"|null, opened: boolean, sort: string } }
// panelOrder: { left: [...widgetNames], right: [...widgetNames] }

const DEFAULT_ORDER = {};
let defaultIdx = 0;
PINBOARD_REGISTRY.forEach((group) =>
  group.widgets.forEach((w) => {
    DEFAULT_ORDER[w.name] = defaultIdx;
    defaultIdx += 1;
  }),
);

function initPanelOrder(widgets) {
  const order = { left: [], right: [] };
  Object.entries(widgets)
    .filter(([, v]) => v.panel === 'left' || v.panel === 'right')
    .sort(
      ([a], [b]) =>
        (DEFAULT_ORDER[a] != null ? DEFAULT_ORDER[a] : 999) -
        (DEFAULT_ORDER[b] != null ? DEFAULT_ORDER[b] : 999),
    )
    .forEach(([name, v]) => {
      order[v.panel].push(name);
    });
  return order;
}

const savedWidgets = ls.get(LS_KEY, {});
const savedPanelOrder = ls.get(PANEL_ORDER_KEY, null);

export default {
  namespaced: true,
  state: {
    widgets: savedWidgets,
    panelOrder: savedPanelOrder || initPanelOrder(savedWidgets),
  },
  getters: {
    getPinnedPanel: (state) => (widget) => (state.widgets[widget] || {}).panel || null,
    getSectionsForPanel: (state) => (panel) => {
      const pinned = new Set(
        Object.entries(state.widgets)
          .filter(([, v]) => v.panel === panel)
          .map(([k]) => k),
      );
      return (state.panelOrder[panel] || []).filter((n) => pinned.has(n));
    },
    isOpened: (state) => (key) => !!(state.widgets[key] || {}).opened,
    getWidgetSort: (state) => (widget) => (state.widgets[widget] || {}).sort || 'biggest-change',
    getWidgetShowPositions: (state) => (widget) => !!(state.widgets[widget] || {}).showPositions,
    getWidgetItemsPerPage: (state) => (widget) => (state.widgets[widget] || {}).itemsPerPage || 10,
  },
  mutations: {
    togglePin(state, { widget, panel = 'left' }) {
      const entry = state.widgets[widget] || {};
      const newPanel = entry.panel === panel ? null : panel;
      state.widgets = { ...state.widgets, [widget]: { ...entry, panel: newPanel } };
      ls.set(LS_KEY, state.widgets);

      if (newPanel && state.panelOrder[newPanel].indexOf(widget) === -1) {
        state.panelOrder = {
          ...state.panelOrder,
          [newPanel]: [...state.panelOrder[newPanel], widget],
        };
        ls.set(PANEL_ORDER_KEY, state.panelOrder);
      }
    },
    toggleOpenPinSection(state, key) {
      const entry = state.widgets[key] || {};
      state.widgets = { ...state.widgets, [key]: { ...entry, opened: !entry.opened } };
      ls.set(LS_KEY, state.widgets);
    },
    reorderWidget(state, { panel, widget, direction }) {
      const pinned = new Set(
        Object.entries(state.widgets)
          .filter(([, v]) => v.panel === panel)
          .map(([k]) => k),
      );
      const fullList = state.panelOrder[panel] || [];
      const displayOrder = fullList.filter((n) => pinned.has(n));
      const displayIdx = displayOrder.indexOf(widget);

      if (direction === 'up' && displayIdx <= 0) return;
      if (direction === 'down' && displayIdx >= displayOrder.length - 1) return;

      const neighbor =
        direction === 'up' ? displayOrder[displayIdx - 1] : displayOrder[displayIdx + 1];
      const newList = fullList.slice();
      const posA = newList.indexOf(widget);
      const posB = newList.indexOf(neighbor);
      newList[posA] = neighbor;
      newList[posB] = widget;

      state.panelOrder = { ...state.panelOrder, [panel]: newList };
      ls.set(PANEL_ORDER_KEY, state.panelOrder);
    },
    setWidgetSort(state, { widget, sort }) {
      const entry = state.widgets[widget] || {};
      state.widgets = { ...state.widgets, [widget]: { ...entry, sort } };
      ls.set(LS_KEY, state.widgets);
    },
    setWidgetShowPositions(state, { widget, showPositions }) {
      const entry = state.widgets[widget] || {};
      state.widgets = { ...state.widgets, [widget]: { ...entry, showPositions } };
      ls.set(LS_KEY, state.widgets);
    },
    setWidgetItemsPerPage(state, { widget, itemsPerPage }) {
      const entry = state.widgets[widget] || {};
      state.widgets = { ...state.widgets, [widget]: { ...entry, itemsPerPage } };
      ls.set(LS_KEY, state.widgets);
    },
  },
  actions: {
    togglePin({ commit }, payload) {
      commit('togglePin', payload);
    },
    toggleOpenPinSection({ commit }, key) {
      commit('toggleOpenPinSection', key);
    },
    reorderWidget({ commit }, payload) {
      commit('reorderWidget', payload);
    },
    setWidgetSort({ commit }, payload) {
      commit('setWidgetSort', payload);
    },
    setWidgetShowPositions({ commit }, payload) {
      commit('setWidgetShowPositions', payload);
    },
    setWidgetItemsPerPage({ commit }, payload) {
      commit('setWidgetItemsPerPage', payload);
    },
  },
};
